import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { DollarSign, Download, CheckCircle, XCircle, Wallet, Edit3 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import PayslipTemplateEditorModal from '../components/PayslipTemplateEditorModal';
import './Users.css';
import './SalaryManagement.css';

const SalaryManagement = () => {
  const { success, error } = useNotifications();
  
  // Helper function to convert decimal hours to HH:MM format
  const formatHoursMinutes = (decimalHours) => {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payroll, setPayroll] = useState(null);
  const [monthlySalaryInput, setMonthlySalaryInput] = useState('0');
  const [savingSalary, setSavingSalary] = useState(false);
  const [overtimeInput, setOvertimeInput] = useState('1.25');
  const [savingOvertime, setSavingOvertime] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [advances, setAdvances] = useState([]);
  const [showAdvances, setShowAdvances] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [processingAdvance, setProcessingAdvance] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingAdvanceId, setRejectingAdvanceId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const rejectCardRef = useRef(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.salaries)) return value.salaries;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await api.get('/users');
      setUsers(normalizeArray(usersData));
    } catch (error) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPayroll = useCallback(async () => {
    if (!selectedUserId || !selectedMonth) {
      setPayroll(null);
      setMonthlySalaryInput('0');
      return;
    }

    try {
      const data = await api.get(`/salary/user/${selectedUserId}/monthly/${selectedMonth}`);
      setPayroll(data);
      setMonthlySalaryInput(String(data?.monthlySalary ?? 0));
      setOvertimeInput(String(data?.overtimeMultiplier ?? 1.25));
    } catch (err) {
      setPayroll(null);
      setMonthlySalaryInput('0');
      error('Unable to load monthly payroll data.');
    }
  }, [selectedUserId, selectedMonth, error]);

  const fetchAdvances = useCallback(async () => {
    try {
      const data = await api.get('/salary/advances');
      setAdvances(normalizeArray(data));
    } catch (err) {
      setAdvances([]);
    }
  }, []);

  useEffect(() => {
    if (rejectModalOpen && rejectCardRef.current) {
      setTimeout(() => {
        rejectCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [rejectModalOpen]);

  const processAdvance = async (id, action, rejectionReason = '') => {
    setProcessingAdvance(id);
    try {
      await api.patch(`/salary/advances/${id}/process`, { action, rejectionReason });
      success(`Advance ${action}d successfully`);
      await fetchAdvances();
    } catch (err) {
      error(err.message || `Failed to ${action} advance`);
    } finally {
      setProcessingAdvance(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#f59e0b', text: 'Pending' },
      approved: { bg: '#10b981', text: 'Approved' },
      rejected: { bg: '#ef4444', text: 'Rejected' },
    };
    return styles[status] || { bg: '#6b7280', text: status };
  };

  useEffect(() => {
    fetchUsers();
    const timer = setInterval(fetchUsers, 30000);
    return () => clearInterval(timer);
  }, [fetchUsers]);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  useEffect(() => {
    if (showAdvances) {
      fetchAdvances();
    }
  }, [showAdvances, fetchAdvances]);

  const saveMonthlySalary = async () => {
    if (!selectedUserId) return;
    const nextSalary = Number(monthlySalaryInput || 0);
    if (!Number.isFinite(nextSalary) || nextSalary < 0) {
      error('The monthly salary must be a valid positive number.');
      return;
    }

    setSavingSalary(true);
    try {
      const data = await api.patch(`/salary/user/${selectedUserId}/monthly/${selectedMonth}/salary`, {
        monthlySalary: nextSalary,
        currency: payroll?.currency || 'TND',
      });
      if (data?.totals) setPayroll(data);
      else await fetchPayroll();
      success('Monthly salary saved. The hourly price and the pay of the month were recalculated.');
    } catch (err) {
      error(err.message || 'Unable to save the monthly salary.');
    } finally {
      setSavingSalary(false);
    }
  };

  // The price of an extra hour is a company setting: it applies to every employee
  const saveOvertimeMultiplier = async () => {
    const next = Number(overtimeInput);
    if (!Number.isFinite(next) || next < 1 || next > 5) {
      error('The extra hour price must be a number between 1 and 5 (for example 1.25).');
      return;
    }
    setSavingOvertime(true);
    try {
      await api.patch('/salary/settings/overtime', { overtimeMultiplier: next });
      await fetchPayroll();
      success(`Extra hours are now paid at ${next} times the normal hour, for all employees.`);
    } catch (err) {
      error(err.message || 'Unable to save the extra hour price.');
    } finally {
      setSavingOvertime(false);
    }
  };

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId],
  );

  const exportPayslip = async () => {
    if (!selectedUserId || !selectedMonth) return;
    setExporting(true);
    try {
      const blob = await api.downloadBlob(`/salary/user/${selectedUserId}/monthly/${selectedMonth}/export`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const userName = selectedUser ? `${selectedUser.firstName || ''}_${selectedUser.lastName || ''}`.trim().replace(/\s+/g, '_') : selectedUserId;
      link.download = `fiche_de_paie_${userName}_${selectedMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      success('Fiche de paie exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      error('Failed to export fiche de paie.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="page-loading">Loading salary workspace...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><DollarSign size={24} /> Salary Management</h1>
      </div>

      <div className="salary-toolbar">
        <div className="form-group" style={{ minWidth: 260 }}>
          <label>Employee</label>
          <select className="tr-select" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
            <option value="">Select employee</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ minWidth: 180 }}>
          <label>Month</label>
          <input className="tr-input" type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
        </div>

        <div className="form-group" style={{ alignSelf: 'end' }}>
          <button className="btn btn-outline" onClick={() => setShowTemplateModal(true)} title="Customize Fiche de Paie Template">
            <Edit3 size={14} /> Modèle Fiche de Paie
          </button>
        </div>

        {payroll && selectedUser && (
          <div className="form-group" style={{ alignSelf: 'end' }}>
            <button className="btn btn-primary" onClick={exportPayslip} disabled={exporting}>
              <Download size={14} /> {exporting ? 'Exporting...' : 'Export fiche de paie'}
            </button>
          </div>
        )}
        <div className="form-group" style={{ alignSelf: 'end' }}>
          <button className="btn btn-secondary" onClick={() => setShowAdvances(!showAdvances)}>
            <Wallet size={14} /> {showAdvances ? 'Hide' : 'Manage'} Advances
          </button>
        </div>
      </div>

      {/* Payslip Template Editor Modal */}
      <PayslipTemplateEditorModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSaveSuccess={() => {
          if (selectedUserId && selectedMonth) fetchPayroll();
        }}
      />

      {/* Salary Advances Management */}
      {showAdvances && (
        <div className="salary-summary-card" style={{ marginBottom: 20 }}>
          <h3>Salary Advance Requests</h3>
          {advances.length === 0 ? (
            <div className="empty-row">No advance requests found.</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Amount</th>
                    <th>Month</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {advances.map((adv) => {
                    const status = getStatusBadge(adv.status);
                    return (
                      <tr key={adv.id}>
                        <td>{adv.user ? `${adv.user.firstName} ${adv.user.lastName}` : `User ${adv.userId}`}</td>
                        <td>{Number(adv.amount).toFixed(2)} TND</td>
                        <td>{adv.month}</td>
                        <td>{adv.reason || '-'}</td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 4,
                            background: status.bg,
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 500,
                          }}>
                            {status.text}
                          </span>
                        </td>
                        <td>{new Date(adv.createdAt).toLocaleDateString()}</td>
                        <td>
                          {adv.status === 'pending' && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="btn btn-success"
                                onClick={() => processAdvance(adv.id, 'approve')}
                                disabled={processingAdvance === adv.id}
                                title="Approve"
                              >
                                <CheckCircle size={14} />
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => {
                                  setRejectingAdvanceId(adv.id);
                                  setRejectReason('');
                                  setRejectModalOpen(true);
                                }}
                                disabled={processingAdvance === adv.id}
                                title="Reject"
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          )}
                          {adv.status === 'approved' && <CheckCircle size={16} color="#10b981" />}
                          {adv.status === 'rejected' && (
                            <span title={adv.rejectionReason || 'No reason'}>
                              <XCircle size={16} color="#ef4444" />
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {rejectModalOpen && (
            <div ref={rejectCardRef} className="reject-reason-card" style={{ marginTop: 16 }}>
              <h4><XCircle size={18} style={{ verticalAlign: 'middle', marginRight: 8, color: '#ef4444' }} /> Reject Advance Request</h4>
              <label className="reason-label">Reason for rejection (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this advance is being rejected..."
                rows={3}
              />
              <div className="card-actions">
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    processAdvance(rejectingAdvanceId, 'reject', rejectReason);
                    setRejectModalOpen(false);
                    setRejectingAdvanceId(null);
                    setRejectReason('');
                  }}
                  disabled={processingAdvance === rejectingAdvanceId}
                >
                  {processingAdvance === rejectingAdvanceId ? 'Rejecting...' : 'Confirm Reject'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setRejectModalOpen(false);
                    setRejectingAdvanceId(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {payroll && selectedUser ? (
        <>
          <div className="salary-summary-card">
            <div className="form-row">
              <div className="form-group">
                <label>Monthly Salary ({payroll.currency})</label>
                <input
                  className="tr-input"
                  type="number"
                  min="0"
                  step="0.001"
                  value={monthlySalaryInput}
                  onChange={(e) => setMonthlySalaryInput(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <button className="btn btn-primary" onClick={saveMonthlySalary} disabled={savingSalary}>
                  {savingSalary ? 'Saving...' : 'Save Monthly Salary'}
                </button>
              </div>
              <div className="form-group">
                <label>Extra hour price (× normal hour)</label>
                <input
                  className="tr-input"
                  type="number"
                  min="1"
                  max="5"
                  step="0.05"
                  value={overtimeInput}
                  onChange={(e) => setOvertimeInput(e.target.value)}
                  title="Applies to every employee"
                />
              </div>
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <button className="btn btn-secondary" onClick={saveOvertimeMultiplier} disabled={savingOvertime}>
                  {savingOvertime ? 'Saving...' : 'Save Extra Hour Price'}
                </button>
              </div>
            </div>

            <p className="salary-rule-note">
              Hourly price this month: <strong>{Number(payroll.hourlyRate || 0).toFixed(3)} {payroll.currency}</strong>
              {' '}= monthly salary ÷ ({payroll.workingDays} working days × {payroll.dailyHours}h = {payroll.standardHours}h).
              Each day is counted in whole hours (30 minutes or more count as one hour). The first {payroll.dailyHours} hours
              of a day are normal hours; the hours above are extra hours, paid × {payroll.overtimeMultiplier}.
              Approved leave on a working day is paid as {payroll.dailyHours} normal hours. The salary saved here applies
              from {selectedMonth} onwards; the extra hour price applies to all employees.
            </p>

            <div className="salary-metrics">
              <div className="metric-box"><span>Normal Hours</span><strong>{payroll.totals?.regularHours ?? 0}h</strong></div>
              <div className="metric-box"><span>Extra Hours</span><strong>{payroll.totals?.overtimeHours ?? 0}h</strong></div>
              <div className="metric-box"><span>Remote Hours</span><strong>{formatHoursMinutes(payroll.totals?.remoteHours ?? 0)}</strong></div>
              <div className="metric-box"><span>Paid Leave Days</span><strong>{payroll.totals?.leaveDays ?? 0}</strong></div>
              <div className="metric-box salary-breakdown">
                <span>Pay of the Month</span>
                <div className="salary-details">
                  <div className="salary-total">{Number(payroll.totals?.grossSalary ?? 0).toFixed(3)} {payroll?.currency || 'TND'}</div>
                  <div className="salary-components">
                    <small>Normal hours: {Number(payroll.totals?.regularPay ?? 0).toFixed(3)}</small>
                    <small>Extra hours: {Number(payroll.totals?.overtimePay ?? 0).toFixed(3)}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="salary-days-list">
            {(payroll.days || []).map((day) => {
              const regularMinutes = day.regularMinutes || 0;
              const overtimeMinutes = day.overtimeMinutes || 0;
              const safeTotal = Math.max(regularMinutes + overtimeMinutes, 1);
              const regularPct = (regularMinutes / safeTotal) * 100;
              const overtimePct = (overtimeMinutes / safeTotal) * 100;
              const isLeave = day.type === 'leave';

              return (
                <div key={day.date} className="salary-day-row">
                  <div className="salary-day-date">{day.date}</div>
                  <div className="salary-day-bar-wrap">
                    <div className="salary-day-bar" title={isLeave ? 'Approved leave, paid as a normal day' : 'Normal hours and extra hours counted for this day'}>
                      <div className="salary-day-regular" style={{ width: `${regularPct}%` }} />
                      <div className="salary-day-overtime" style={{ width: `${overtimePct}%` }} />
                    </div>
                  </div>
                  <div className="salary-day-values">
                    <span className="regular-hours">Normal: {day.regularHours || 0}h</span>
                    <span className="extra-hours">Extra: {day.overtimeHours || 0}h</span>
                    <span className="remote-hours">
                      {isLeave
                        ? 'Approved leave'
                        : `Worked ${formatHoursMinutes(day.totalHours || 0)}${day.remoteMinutes ? ` (remote ${formatHoursMinutes(day.remoteHours || 0)})` : ''}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="users-table-container">
          <div className="empty-row" style={{ padding: 20 }}>
            Select an employee and a month to manage payroll.
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryManagement;
